import { useState, useEffect } from 'react';

export type Theme = 'light' | 'dark';

export interface ThemeColors {
  /** Page and main area background. */
  bg: string;
  /** Sidebar background. */
  sidebarBg: string;
  /** Primary text color. */
  text: string;
  /** Secondary/muted text. */
  textMuted: string;
  /** Border color for separators and cards. */
  border: string;
  /** User message bubble background. */
  userBubble: string;
  /** Assistant message bubble background. */
  assistantBubble: string;
  /** Clarification-question bubble background — visually distinct from an
   * ordinary assistant answer, signaling the agent is waiting on a reply
   * that will resume the paused turn (see clarification-visual-affordance.md). */
  clarificationBubble: string;
  /** Clarification-question bubble border. */
  clarificationBorder: string;
  /** Input field background. */
  inputBg: string;
  /** Input field border. */
  inputBorder: string;
  /** Chat area background. */
  chatBg: string;
  /** Active/selected conversation background. */
  activeConv: string;
  /** Suggestion chip background. */
  suggestionBg: string;
  /** Suggestion chip border. */
  suggestionBorder: string;
  /** Suggestion chip text. */
  suggestionText: string;
  /** Code block background. */
  codeBg: string;
  /** Code block header border. */
  codeHeaderBorder: string;
  /** Table header background. */
  tableHeaderBg: string;
  /** Inline code background. */
  inlineCodeBg: string;
  /** Overlay/backdrop color. */
  overlay: string;
  /** Hamburger button background. */
  hamburgerBg: string;
}

const lightColors: ThemeColors = {
  bg: '#ffffff',
  sidebarBg: '#fafafa',
  text: '#1a1a1a',
  textMuted: '#666666',
  border: '#eeeeee',
  userBubble: '#e3f2fd',
  assistantBubble: '#f1f8e9',
  clarificationBubble: '#fff8e1',
  clarificationBorder: '#ffb300',
  inputBg: '#ffffff',
  inputBorder: '#dddddd',
  chatBg: '#ffffff',
  activeConv: '#e3f2fd',
  suggestionBg: '#e8f5e9',
  suggestionBorder: '#c8e6c9',
  suggestionText: '#2e7d32',
  codeBg: '#1e1e1e',
  codeHeaderBorder: '#333333',
  tableHeaderBg: '#f5f5f5',
  inlineCodeBg: '#e8e8e8',
  overlay: 'rgba(0,0,0,0.3)',
  hamburgerBg: '#ffffff',
};

const darkColors: ThemeColors = {
  bg: '#1e1e2e',
  sidebarBg: '#181825',
  text: '#f0f0f0',
  textMuted: '#b0b0b0',
  border: '#3b3b52',
  userBubble: '#2b3e50',
  assistantBubble: '#1e3320',
  clarificationBubble: '#3d3418',
  clarificationBorder: '#d4a017',
  inputBg: '#2a2a3c',
  inputBorder: '#4a4a5e',
  chatBg: '#232336',
  activeConv: '#2b3e50',
  suggestionBg: '#263326',
  suggestionBorder: '#4a7a4a',
  suggestionText: '#a5d6a7',
  codeBg: '#0d1117',
  codeHeaderBorder: '#3b3b52',
  tableHeaderBg: '#2a2a3c',
  inlineCodeBg: '#3b3b52',
  overlay: 'rgba(0,0,0,0.6)',
  hamburgerBg: '#181825',
};

/**
 * Detects system color scheme preference and returns the appropriate theme colors.
 * Supports manual override stored in localStorage ('mrpibb_theme').
 * Toggle cycles between light/dark. Defaults to system setting.
 */
export function useTheme(): { theme: Theme; colors: ThemeColors; setThemePreference: (pref: 'light' | 'dark') => void } {
  const [override, setOverride] = useState<Theme | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('mrpibb_theme') as Theme | null;
  });

  const [systemTheme, setSystemTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const setThemePreference = (pref: 'light' | 'dark') => {
    setOverride(pref);
    localStorage.setItem('mrpibb_theme', pref);
  };

  const theme: Theme = override ?? systemTheme;

  return { theme, colors: theme === 'dark' ? darkColors : lightColors, setThemePreference };
}
